<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useItemsStore } from '../stores/items';
import SearchBar from '../components/SearchBar.vue';
import ItemsGrid from '../components/ItemsGrid.vue';
import FiltersDrawer from '../components/FiltersDrawer.vue';
import ItemModal from '../components/ItemModal.vue';

const items = useItemsStore();
const route = useRoute();
const filtersOpen = ref(false);

const slug = computed(() => (route.name === 'item' ? String(route.params.slug ?? '') : ''));

// Быстрые фильтры по пулам — горизонтальное «колесо» под хедером.
// slug=null → «Всё» (сбрасывает пулы). Одиночный выбор; повторный тап снимает.
const QUICK_POOLS: { slug: string | null; emoji: string; label: string }[] = [
  { slug: null,           emoji: '🎲', label: 'Все' },
  { slug: 'devil_room',   emoji: '😈', label: 'Дьявол' },
  { slug: 'angel_room',   emoji: '😇', label: 'Ангел' },
  { slug: 'shop',         emoji: '🛒', label: 'Магазин' },
  { slug: 'treasure_room',emoji: '💎', label: 'Сокровищница' },
  { slug: 'boss',         emoji: '👑', label: 'Босс' },
  { slug: 'secret_room',  emoji: '🤫', label: 'Секрет' },
  { slug: 'curse_room',   emoji: '💀', label: 'Проклятье' },
  { slug: 'library',      emoji: '📚', label: 'Библиотека' },
  { slug: 'planetarium',  emoji: '🔮', label: 'Планетарий' },
];

function quickActive(s: string | null) {
  return s === null
    ? items.filters.pools.length === 0
    : items.filters.pools.length === 1 && items.filters.pools[0] === s;
}

function selectQuick(s: string | null) {
  items.filters.pools = s === null || quickActive(s) ? [] : [s];
}

const activeFilters = computed(() =>
  (items.filters.kind ? 1 : 0)
  + items.filters.quality.length
  + items.filters.pools.length,
);

onMounted(() => items.init());
</script>

<template>
  <div class="flex h-full flex-col">
    <header class="border-b border-stone-800 bg-bg pt-safe">
      <div class="flex items-center gap-2 px-3 py-2">
        <h1 class="text-sm font-semibold uppercase tracking-wide text-stone-300">Isaac Items</h1>
        <span class="text-xs text-stone-500">{{ items.total }}</span>
        <div class="ml-auto flex items-center gap-2">
          <RouterLink
            to="/scan"
            class="rounded-md bg-bg-card px-3 py-1.5 text-sm text-stone-200 active:bg-bg-elev"
            aria-label="Сканер"
          >
            📷
          </RouterLink>
          <button
            class="relative rounded-md bg-bg-card px-3 py-1.5 text-sm text-stone-200 active:bg-bg-elev"
            @click="filtersOpen = true"
          >
            Фильтры
            <span
              v-if="activeFilters > 0"
              class="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-stone-900"
            >{{ activeFilters }}</span>
          </button>
        </div>
      </div>
      <div class="px-3 pb-2">
        <SearchBar />
      </div>
      <div class="no-scrollbar flex gap-2 overflow-x-auto px-3 pb-2">
        <button
          v-for="f in QUICK_POOLS"
          :key="String(f.slug)"
          @click="selectQuick(f.slug)"
          :aria-pressed="quickActive(f.slug)"
          class="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors"
          :class="quickActive(f.slug)
            ? 'bg-stone-200 text-stone-900'
            : 'bg-bg-card text-stone-300 active:bg-bg-elev'"
        >
          <span class="text-base leading-none">{{ f.emoji }}</span>
          {{ f.label }}
        </button>
      </div>
    </header>

    <main class="relative flex-1 overflow-hidden">
      <ItemsGrid />
    </main>

    <FiltersDrawer :open="filtersOpen" @close="filtersOpen = false" />

    <ItemModal v-if="slug" :slug="slug" :key="slug" />
  </div>
</template>
